import React, { useState } from 'react';
import { X, ChevronRight, ChevronLeft, Sparkles, Target, Mic, MessageSquare, Award } from 'lucide-react';
import clsx from 'clsx';

const OnboardingFlow = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);

  if (!isOpen) return null;

  const steps = [
    {
      title: 'Добро пожаловать в Savitskaya.ai!',
      description: 'Платформа для тренировки навыков продаж через реалистичные AI-диалоги',
      icon: <Sparkles className="w-12 h-12" />,
      content: (
        <div className="space-y-4">
          <p className="text-slate-700 leading-relaxed">
            Вы будете разговаривать с AI-собеседником в различных сценариях:
            звонки секретарям, переговоры с ЛПР, работа с возражениями.
          </p>
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-200">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-white rounded-lg">
                <Sparkles className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-1">AI анализирует каждое слово</h4>
                <p className="text-sm text-slate-600">
                  Получайте подсказки в реальном времени и детальную обратную связь после каждой тренировки
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Как работает тренировка',
      description: 'Простой процесс: выберите сценарий, позвоните, получите анализ',
      icon: <Target className="w-12 h-12" />,
      content: (
        <div className="space-y-4">
          <div className="space-y-3">
            <Step
              number={1}
              title="Выбор сценария"
              description="Выберите ситуацию для тренировки: холодный звонок, работа с возражениями и др."
            />
            <Step
              number={2}
              title="Звонок с AI"
              description="Разговаривайте голосом или печатайте. AI играет роль секретаря или ЛПР."
            />
            <Step
              number={3}
              title="Получение обратной связи"
              description="После звонка AI анализирует диалог и дает конкретные рекомендации."
            />
          </div>
        </div>
      ),
    },
    {
      title: 'AI подсказки в реальном времени',
      description: 'Во время диалога AI будет помогать вам',
      icon: <MessageSquare className="w-12 h-12" />,
      content: (
        <div className="space-y-4">
          <p className="text-slate-700 leading-relaxed">
            Во время тренировки справа вы увидите панель с:
          </p>
          <div className="space-y-3">
            <Feature
              icon={<Target className="w-5 h-5 text-blue-600" />}
              title="Цели звонка"
              description="Список задач, которые нужно выполнить. AI автоматически отметит достигнутые цели."
            />
            <Feature
              icon={<Sparkles className="w-5 h-5 text-purple-600" />}
              title="AI подсказки"
              description="Контекстные советы что делать дальше. Обновляются после каждого вашего сообщения."
            />
            <Feature
              icon={<Award className="w-5 h-5 text-yellow-600" />}
              title="Метрики"
              description="Вовлеченность, прогресс по целям и темп диалога в реальном времени."
            />
          </div>
        </div>
      ),
    },
    {
      title: 'Голосовой ввод',
      description: 'Говорите естественно - как в реальном звонке',
      icon: <Mic className="w-12 h-12" />,
      content: (
        <div className="space-y-4">
          <p className="text-slate-700 leading-relaxed">
            Для максимального погружения используйте голосовой ввод:
          </p>
          <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
            <div className="flex items-center justify-center mb-4">
              <div className="relative">
                <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center">
                  <Mic className="w-10 h-10 text-white" />
                </div>
                <div className="absolute inset-0 bg-blue-400 rounded-full animate-ping opacity-25"></div>
              </div>
            </div>
            <p className="text-sm text-center text-slate-600">
              Нажмите на кнопку микрофона и говорите.
              AI распознает речь и ответит голосом.
            </p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
            <p className="text-sm text-yellow-800">
              <strong>Совет:</strong> Можно также печатать текстом - это удобно для тихих мест или если нет микрофона.
            </p>
          </div>
        </div>
      ),
    },
    {
      title: 'Готовы начать?',
      description: 'Выберите первый сценарий и начните тренировку',
      icon: <Award className="w-12 h-12" />,
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6 border border-green-200">
            <div className="text-center">
              <div className="inline-flex p-3 bg-white rounded-full mb-4">
                <Award className="w-8 h-8 text-green-600" />
              </div>
              <h4 className="text-lg font-semibold text-slate-900 mb-2">
                Первые тренировки самые важные
              </h4>
              <p className="text-sm text-slate-600 mb-4">
                Начните с простого сценария "Холодный звонок секретарю".
                С каждой тренировкой вы будете улучшать свои навыки!
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <TipItem text="Не бойтесь ошибаться - это тренировка, а не реальный звонок" />
            <TipItem text="Обращайте внимание на AI подсказки - они помогут вам" />
            <TipItem text="После звонка изучите детальный анализ с рекомендациями" />
          </div>
        </div>
      ),
    },
  ];

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      // Mark onboarding as completed in localStorage
      localStorage.setItem('onboarding_completed', 'true');
      onComplete();
      onClose();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('onboarding_completed', 'true');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white rounded-xl shadow-sm text-blue-600">
                {step.icon}
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {step.title}
                </h2>
                <p className="text-sm text-slate-600 mt-0.5">
                  {step.description}
                </p>
              </div>
            </div>
            <button
              onClick={handleSkip}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-white rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Progress */}
          <div className="flex gap-1.5">
            {steps.map((_, index) => (
              <div
                key={index}
                className={clsx(
                  'h-1.5 rounded-full transition-all duration-300',
                  index <= currentStep
                    ? 'bg-blue-500 flex-1'
                    : 'bg-slate-200 flex-1'
                )}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step.content}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-200 bg-slate-50 flex justify-between items-center">
          <button
            onClick={handleSkip}
            className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
          >
            Пропустить обучение
          </button>

          <div className="flex gap-3">
            {!isFirstStep && (
              <button
                onClick={handlePrev}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors flex items-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Назад
              </button>
            )}
            <button
              onClick={handleNext}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium shadow-sm"
            >
              {isLastStep ? 'Начать тренировку' : 'Далее'}
              {!isLastStep && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Components
const Step = ({ number, title, description }) => (
  <div className="flex items-start gap-4 bg-white rounded-lg p-4 border border-slate-200">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-sm">
      {number}
    </div>
    <div>
      <h4 className="font-semibold text-slate-900 mb-1">{title}</h4>
      <p className="text-sm text-slate-600">{description}</p>
    </div>
  </div>
);

const Feature = ({ icon, title, description }) => (
  <div className="flex items-start gap-3 bg-slate-50 rounded-lg p-3 border border-slate-200">
    <div className="flex-shrink-0 p-2 bg-white rounded-lg">
      {icon}
    </div>
    <div>
      <h4 className="font-semibold text-slate-900 text-sm mb-1">{title}</h4>
      <p className="text-xs text-slate-600">{description}</p>
    </div>
  </div>
);

const TipItem = ({ text }) => (
  <div className="flex items-center gap-2 text-sm text-slate-600">
    <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
    <span>{text}</span>
  </div>
);

export default OnboardingFlow;
